if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const formData = new FormData(this.form);
        const proEditionOption = this.selectedProEditionOption;
        const proEditionError = this.getProEditionValidationError(proEditionOption);
        if (proEditionError) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: proEditionError,
            message: proEditionError,
          });
          this.handleErrorMessage(proEditionError);
          console.error('Pro Edition upsell configuration error:', {
            option: proEditionOption,
            error: proEditionError,
          });
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        if (this.isPaidProEditionOption(proEditionOption)) {
          this.submitProEditionAdd(formData, proEditionOption);
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            this.handleCartAddResponse(response, formData.get('id'));
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.resetSubmitButton();
          });
      }

      async submitProEditionAdd(formData, proEditionOption) {
        const hardwareVariantId = formData.get('id');

        try {
          const hardwareItem = this.buildCartItemFromFormData(formData);
          const proEditionItem = this.buildProEditionCartItem(proEditionOption);
          const items = [hardwareItem];
          const proAlreadyInCart = await this.proEditionItemIsInCart(proEditionItem);

          // v1 only avoids incrementing an exact Pro line; switching Pro plans remains a cart-level action.
          if (!proAlreadyInCart) items.push(proEditionItem);

          const body = { items };

          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(body);

          const response = await fetch(`${routes.cart_add_url}`, config);
          const parsedState = await response.json();
          const cartState = this.getCartAddStateForNotification(parsedState, hardwareVariantId);
          this.handleCartAddResponse(cartState, hardwareVariantId);
        } catch (e) {
          console.error(e);
        } finally {
          this.resetSubmitButton();
        }
      }

      handleCartAddResponse(response, productVariantId) {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButtonText.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        } else if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId,
            cartData: response,
          });
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                this.cart.renderContents(response);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          this.cart.renderContents(response);
        }
      }

      resetSubmitButton() {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      buildCartItemFromFormData(formData) {
        const item = {
          id: formData.get('id'),
          quantity: Number.parseInt(formData.get('quantity'), 10) || 1,
        };
        const sellingPlan = formData.get('selling_plan');
        const properties = {};

        if (sellingPlan) item.selling_plan = sellingPlan;

        formData.forEach((value, key) => {
          const propertyName = key.match(/^properties\[(.*)\]$/)?.[1];
          if (!propertyName || value === '') return;

          properties[propertyName] = value;
        });

        if (Object.keys(properties).length > 0) item.properties = properties;

        return item;
      }

      buildProEditionCartItem(proEditionOption) {
        const item = {
          id: proEditionOption.variantId,
          quantity: 1,
        };

        if (proEditionOption.sellingPlanId) item.selling_plan = proEditionOption.sellingPlanId;

        return item;
      }

      async proEditionItemIsInCart(proEditionItem) {
        let cart;

        try {
          const response = await fetch(this.cartJsonUrl, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`Cart lookup failed with status ${response.status}`);
          cart = await response.json();
        } catch (error) {
          console.warn('Unable to check for an existing Pro Edition cart line before add.', error);
          return false;
        }

        return cart.items?.some((item) => {
          const itemSellingPlanId = item.selling_plan_allocation?.selling_plan?.id || '';

          return (
            String(item.variant_id) === String(proEditionItem.id) &&
            String(itemSellingPlanId) === String(proEditionItem.selling_plan || '')
          );
        });
      }

      getCartAddStateForNotification(parsedState, hardwareVariantId) {
        if (!parsedState?.items || parsedState.key) return parsedState;

        const hardwareItem =
          parsedState.items.find((item) => String(item.variant_id || item.id) === String(hardwareVariantId)) ||
          parsedState.items[0];

        if (!hardwareItem) return parsedState;

        return {
          ...parsedState,
          id: hardwareItem.id,
          key: hardwareItem.key,
        };
      }

      getProEditionValidationError(proEditionOption) {
        if (!this.isPaidProEditionOption(proEditionOption)) return false;

        if (proEditionOption.disabled) {
          return (
            proEditionOption.errorMessage ||
            'The selected Pro Edition option is temporarily unavailable. Please choose another option or Basic Edition.'
          );
        }

        if (!proEditionOption.variantId) {
          return (
            proEditionOption.errorMessage ||
            'The selected Pro Edition option is temporarily unavailable. Please choose another option or Basic Edition.'
          );
        }

        if (proEditionOption.requiresSellingPlan && !proEditionOption.sellingPlanId) {
          return (
            proEditionOption.errorMessage ||
            'The selected Pro Edition subscription is temporarily unavailable. Please choose another option or Basic Edition.'
          );
        }

        return false;
      }

      isPaidProEditionOption(proEditionOption) {
        return proEditionOption && proEditionOption.value && proEditionOption.value !== 'basic';
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get selectedProEditionOption() {
        const upsell = this.proEditionUpsell;
        if (!upsell) return null;
        if (upsell.selectedOption) return upsell.selectedOption;

        const input = upsell.querySelector('.pro-edition-upsell__radio:checked');
        if (!input) return null;

        return {
          value: input.value,
          variantId: input.dataset.proVariantId || '',
          sellingPlanId: input.dataset.sellingPlanId || '',
          requiresSellingPlan: input.dataset.requiresSellingPlan === 'true',
          label: input.dataset.proLabel || input.value,
          errorMessage: input.dataset.errorMessage || '',
          disabled: input.disabled,
        };
      }

      get proEditionUpsell() {
        const formId = this.form?.getAttribute('id');
        if (!formId) return null;

        return Array.from(document.querySelectorAll('pro-edition-upsell')).find(
          (upsell) => upsell.dataset.productFormId === formId
        );
      }

      get cartJsonUrl() {
        const cartUrl = window.routes?.cart_url || '/cart';
        return `${cartUrl.split('?')[0].replace(/\/$/, '')}.js`;
      }
    }
  );
}
